import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { getTranslation } from '../../../utils/translations';
import userRoleService from '../../../services/userRoleService';
import { toast } from 'sonner';

const getUserMetaFromAssignment = (assignment) => {
  const u = assignment?.user;
  const name = (u?.full_name || u?.fullName || '')?.trim?.() || '';
  const email = (u?.email || '')?.trim?.() || '';
  return { name, email };
};

const RoleAssignmentsSection = ({ roleAssignments, onRefresh, language }) => {
  const t = getTranslation(language);
  const ai = t?.adminIdentity || {};
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [pendingRemoveAssignmentId, setPendingRemoveAssignmentId] = useState(null);

  const getRoleColor = (roleType) => {
    const colors = {
      dentist_client: 'bg-blue-100 text-blue-700',
      dentist_supplier: 'bg-green-100 text-green-700',
      distributor: 'bg-purple-100 text-purple-700',
      administrator: 'bg-red-100 text-red-700',
      production_supplier: 'bg-orange-100 text-orange-700',
      production_responsible: 'bg-amber-100 text-amber-800',
      logistics_coordinator: 'bg-teal-100 text-teal-800',
    };
    return colors?.[roleType] || 'bg-gray-100 text-gray-700';
  };

  const groupedSorted = useMemo(() => {
    const acc = {};
    for (const a of roleAssignments || []) {
      const uid = a?.userId;
      if (!uid) continue;
      if (!acc[uid]) acc[uid] = [];
      acc[uid].push(a);
    }
    return Object.entries(acc).sort(([idA, listA], [idB, listB]) => {
      const { name: na, email: ea } = getUserMetaFromAssignment(listA?.[0]);
      const { name: nb, email: eb } = getUserMetaFromAssignment(listB?.[0]);
      const la = (na || ea || idA || '').toLowerCase();
      const lb = (nb || eb || idB || '').toLowerCase();
      return la.localeCompare(lb, undefined, { sensitivity: 'base' });
    });
  }, [roleAssignments]);

  const matchesAssignment = (a, userId, qLower) => {
    if (activeFilter === 'active' && !a?.isActive) return false;
    if (!qLower) return true;
    const { name, email } = getUserMetaFromAssignment(a);
    const roleName = (a?.role?.roleName || '').toLowerCase();
    const roleType = (a?.role?.roleType || '').toLowerCase();
    const country = (a?.country?.countryName || '').toLowerCase();
    return (
      userId?.toLowerCase()?.includes(qLower) ||
      name.toLowerCase().includes(qLower) ||
      email.toLowerCase().includes(qLower) ||
      roleName.includes(qLower) ||
      roleType.includes(qLower) ||
      country.includes(qLower)
    );
  };

  const filteredGroups = useMemo(() => {
    const qLower = search.trim().toLowerCase();
    return groupedSorted
      ?.map(([userId, assignments]) => [
        userId,
        assignments?.filter((a) => matchesAssignment(a, userId, qLower)),
      ])
      ?.filter(([, list]) => list?.length > 0);
  }, [groupedSorted, search, activeFilter]);

  const handleRemoveAssignmentConfirmed = async (assignmentId) => {
    try {
      await userRoleService?.removeRoleAssignment(assignmentId);
      setPendingRemoveAssignmentId(null);
      await onRefresh?.();
      toast.success(language === 'FR' ? 'Affectation retirée' : 'Assignment removed');
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast.error(language === 'FR' ? 'Échec' : 'Failed', {
        description: error?.message || String(error),
      });
    }
  };

  const handleToggleActive = async (assignment) => {
    try {
      await userRoleService?.updateRoleAssignment(assignment?.id, {
        isActive: !assignment?.isActive,
      });
      await onRefresh?.();
    } catch (error) {
      console.error('Error toggling assignment:', error);
      toast.error(language === 'FR' ? 'Échec' : 'Failed', {
        description: error?.message || String(error),
      });
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-foreground">
        {language === 'FR' ? 'Attributions de rôles' : 'Role assignments'}
      </h2>

      <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-stretch sm:items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            label={language === 'FR' ? 'Recherche' : 'Search'}
            type="text"
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            placeholder={ai.assignmentsSearchPlaceholder}
          />
        </div>
        <div className="flex rounded-lg border border-border p-1 bg-muted/30">
          <button
            type="button"
            onClick={() => setActiveFilter('all')}
            className={`px-3 py-2 text-sm rounded-md transition-colors ${
              activeFilter === 'all'
                ? 'bg-background shadow-sm font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {ai.assignmentShowAll}
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter('active')}
            className={`px-3 py-2 text-sm rounded-md transition-colors ${
              activeFilter === 'active'
                ? 'bg-background shadow-sm font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {ai.assignmentActiveOnly}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredGroups?.map(([userId, assignments]) => {
          const { name: displayName, email: displayEmail } = getUserMetaFromAssignment(assignments?.[0]);
          const heading =
            displayName ||
            displayEmail ||
            `${ai.userIdFallback} (${userId?.substring(0, 8)}…)`;

          return (
            <div key={userId} className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-foreground text-lg">{heading}</h3>
                  {displayName && displayEmail && (
                    <p className="text-sm text-muted-foreground">{displayEmail}</p>
                  )}
                  {!displayName && displayEmail && (
                    <p className="text-sm text-muted-foreground">{displayEmail}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {assignments?.length}{' '}
                    {language === 'FR'
                      ? 'affectation(s) affichée(s)'
                      : 'assignment(s) shown'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignments?.map((assignment) => (
                  <div key={assignment?.id} className="border border-border rounded-lg p-4 flex flex-col gap-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                          assignment?.role?.roleType
                        )}`}
                      >
                        {assignment?.role?.roleName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(assignment)}
                        className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border transition-colors ${
                          assignment?.isActive
                            ? 'border-success/40 bg-success/10 text-success'
                            : 'border-border bg-muted text-muted-foreground'
                        }`}
                      >
                        <Icon
                          name={assignment?.isActive ? 'CheckCircle' : 'Circle'}
                          size={16}
                        />
                        {assignment?.isActive
                          ? ai.assignmentStatusActive
                          : ai.assignmentStatusInactive}
                      </button>
                    </div>

                    {assignment?.country && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span>{assignment?.country?.countryName}</span>
                      </div>
                    )}

                    {assignment?.regionAccess?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {assignment?.regionAccess?.map((region, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded text-xs bg-muted text-foreground"
                          >
                            {region}
                          </span>
                        ))}
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => setPendingRemoveAssignmentId(assignment?.id)}
                      iconName="Trash2"
                      iconPosition="left"
                      fullWidth
                    >
                      {t?.roles?.removeRole}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(roleAssignments || []).length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
          <Icon name="UserCheck" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      )}

      {pendingRemoveAssignmentId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div
            role="dialog"
            aria-labelledby="confirm-remove-role-title"
            className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4"
          >
            <h3 id="confirm-remove-role-title" className="text-lg font-semibold text-foreground">
              {ai.confirmRemoveAssignmentTitle}
            </h3>
            <p className="text-sm text-muted-foreground">{ai.confirmRemoveAssignmentBody}</p>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingRemoveAssignmentId(null)}
              >
                {language === 'FR' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleRemoveAssignmentConfirmed(pendingRemoveAssignmentId)}
              >
                {t?.common?.confirm || 'OK'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAssignmentsSection;
